import { useSyncExternalStore } from "react";
import type { ThemePreference } from "@app-factory/shared";

/**
 * Volba motivu. Tři stavy, ne dva — „podle systému" je vlastní volba, ne
 * odvozenina: uživatel, který ji nechá zapnutou, chce, aby se aplikace
 * přepnula s ním, a to i za běhu. **Výchozí je tmavý**, ne systémový.
 *
 * Volba patří účtu (`User.themePreference`), ne prohlížeči: na sdíleném
 * stroji by si ji dva lidé přepisovali navzájem a na jiném počítači by se
 * začínalo znovu. `localStorage` slouží jen jako předsádka — inline skript
 * v `index.html` z něj přečte poslední známou volbu a zapíše motiv ještě
 * před prvním paintem (žádný záblesk), server ji hned nato potvrdí nebo
 * opraví.
 *
 * CSS proto zná jen `:root` (tmavý) a `:root[data-theme="light"]` —
 * žádnou media query.
 */

export type ThemeChoice = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

/** Musí sedět s klíčem v inline skriptu v index.html. */
const STORAGE_KEY = "af.theme";

const systemQuery =
  typeof window !== "undefined" ? window.matchMedia("(prefers-color-scheme: light)") : null;

function readChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light" || stored === "system") return stored;
  } catch {
    // privátní režim / zakázané úložiště — zůstane výchozí tmavý
  }
  return "dark";
}

/** Převod mezi hodnotou účtu (`DARK`) a volbou v klientovi (`dark`). */
export function fromPreference(pref: ThemePreference): ThemeChoice {
  return pref.toLowerCase() as ThemeChoice;
}
export function toPreference(next: ThemeChoice): ThemePreference {
  return next.toUpperCase() as ThemePreference;
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  if (choice !== "system") return choice;
  return systemQuery?.matches ? "light" : "dark";
}

let choice: ThemeChoice = readChoice();
const listeners = new Set<() => void>();

function apply(resolved: ResolvedTheme) {
  document.documentElement.dataset.theme = resolved;
  // Barva systémového chromu na mobilu — bez ní zůstane lišta prohlížeče tmavá
  // i ve světlém motivu.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", resolved === "light" ? "#eef2f9" : "#05070d");
}

function setLocal(next: ThemeChoice) {
  choice = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // neuložená volba platí aspoň pro tuhle relaci
  }
  apply(resolveTheme(next));
  for (const listener of listeners) listener();
}

export function setThemeChoice(next: ThemeChoice) {
  setLocal(next);
}

/**
 * Srovnání s účtem po přihlášení. Volá se ze session dotazu; když se hodnoty
 * liší, vyhrává účet — přihlášení na cizím stroji musí ukázat můj motiv.
 */
export function syncThemeFromAccount(pref: ThemePreference) {
  const fromAccount = fromPreference(pref);
  if (fromAccount !== choice) setLocal(fromAccount);
}

// Systém se může přepnout za běhu (macOS podle času). Reagujeme jen tehdy,
// když si uživatel motiv nevynutil ručně.
systemQuery?.addEventListener("change", () => {
  if (choice !== "system") return;
  apply(resolveTheme("system"));
  for (const listener of listeners) listener();
});

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): { choice: ThemeChoice; resolved: ResolvedTheme } {
  const current = useSyncExternalStore(
    subscribe,
    () => choice,
    () => "system" as ThemeChoice,
  );
  return { choice: current, resolved: resolveTheme(current) };
}

/** Popisky pro přepínač v uživatelském menu. */
export const THEME_LABELS: Record<ThemeChoice, string> = {
  light: "SVĚTLÝ",
  dark: "TMAVÝ",
  system: "SYSTÉM",
};
